import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { GOOGLE_FLOW_KEY, googleApi, googleHeaders, safeReturnTo } from '../utils/googleAuth';
import { useAuthStore } from '../store/authStore';
export default function GoogleAuthCallback() {
  const navigate = useNavigate();
  const handled = useRef(false);
  const destination = useRef('/apps');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [signup, setSignup] = useState(null);
  const [profile, setProfile] = useState({ nombre: '', apellido: '' });
  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const params = new URLSearchParams(window.location.search);
    let flow;
    try { flow = JSON.parse(sessionStorage.getItem(GOOGLE_FLOW_KEY)); } catch { flow = null; }
    sessionStorage.removeItem(GOOGLE_FLOW_KEY);
    window.history.replaceState(window.history.state, '', window.location.pathname);
    if (params.has('error') || !params.get('code') || !flow?.state || params.get('state') !== flow.state ||
      !flow.browserProof || !['login', 'link'].includes(flow.purpose) || !Number.isFinite(flow.startedAt) || Date.now() - flow.startedAt > 600000) {
      setError('No se completó la autenticación. Vuelve a continuar con Google.');
      setBusy(false);
      return;
    }
    destination.current = safeReturnTo(flow.returnTo);
    googleApi.post(flow.purpose === 'link' ? '/auth/google/link/complete' : '/auth/google/complete',
      { state: flow.state, code: params.get('code'), browserProof: flow.browserProof }, flow.purpose === 'link' ? googleHeaders() : undefined)
      .then(({ data }) => {
        if (flow.purpose === 'link') { navigate(destination.current.startsWith('/forms/') ? destination.current : '/account/google', { replace: true, state: { googleLinked: true } }); return; }
        if (data.signupRequired) { setSignup(data); setProfile({ nombre: data.profile.nombre || '', apellido: data.profile.apellido || '' }); return; }
        useAuthStore.getState().acceptSession(data);
        navigate(destination.current, { replace: true });
      })
      .catch(e => setError(e.response?.data?.message || 'No se pudo iniciar sesión con Google. Inténtalo nuevamente.'))
      .finally(() => setBusy(false));
  }, [navigate]);
  async function register(event) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const { data } = await googleApi.post('/auth/google/register', { signupToken: signup.signupToken, nombre: profile.nombre.trim(), apellido: profile.apellido.trim() });
      useAuthStore.getState().acceptSession(data);
      navigate(destination.current, { replace: true });
    } catch (e) { setError(e.response?.data?.message || 'No se pudo crear tu cuenta.'); }
    finally { setBusy(false); }
  }
  return <Stack alignItems="center" justifyContent="center" minHeight="70vh" sx={{ p: 2 }}>
    <Card sx={{ p: 4, width: '100%', maxWidth: 420, boxSizing: 'border-box' }}>
      <Stack spacing={2}>
        <Typography variant="h5" component="h1">{signup ? 'Completa tu cuenta' : 'Continuar con Google'}</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {busy && <CircularProgress aria-label="Procesando autenticación" />}
        {signup && <form onSubmit={register}><Stack spacing={2}>
          <Typography>Confirma tus datos para crear tu cuenta DevsSoft.</Typography>
          <TextField label="Correo de Google" value={signup.profile.email} fullWidth InputProps={{ readOnly: true }} />
          <TextField label="Nombre" required fullWidth value={profile.nombre} inputProps={{ maxLength: 100 }}
            onChange={e => setProfile({ ...profile, nombre: e.target.value })} />
          <TextField label="Apellido" required fullWidth value={profile.apellido} inputProps={{ maxLength: 100 }}
            onChange={e => setProfile({ ...profile, apellido: e.target.value })} />
          <Button type="submit" variant="contained" disabled={busy}>Crear mi cuenta</Button>
        </Stack></form>}
        {error && <Button component={RouterLink} to="/login">Volver a iniciar sesión</Button>}
        {error && useAuthStore.getState().isAuthenticated && <Button component={RouterLink} to="/account/google">Ir a mi cuenta</Button>}
      </Stack>
    </Card>
  </Stack>;
}
